import sortBy from "lodash/sortBy";
import groupBy from "lodash/groupBy";
import union from "lodash/union";
import difference from "lodash/difference";
import isEmpty from "lodash/isEmpty";

import { US_CURRENCY_CODE } from "@constants/store";
import { isSwagProduct } from "@helpers/products/types";
import { pluralizeWord } from "@stadium/helpers/string";

import { convertPrice, formatNumberToShortScale, formatPrice } from "@helpers/currency";

import {
  ADDITIONAL_ON_DEMAND_FILTER_TYPE,
  BULK_ITEM_FILTER_TYPE,
  CORE_ON_DEMAND_FILTER_TYPE,
  ECO_FRIENDLY_FILTER,
  PRODUCT_PRICE_SLIDER_STEP,
  SHIPPED_FROM_STADIUM_FILTER,
  SUSTAINABLE_FILTER,
  LOCKER_INVENTORY_FILTER_TYPE,
  MY_DESIGNED_PRODUCTS_FILTER,
  DESIGNED_BY_OTHERS_FILTER,
  ON_DEMAND_FILTER_TYPE,
  VOLUME_DISCOUNT_FILTER,
  DECORATION_METHOD_KEY,
  PRODUCTION_DAYS_FILTER_KEY,
  ITEM_TYPE_FILTER_KEY,
  ITEM_FEATURES_FILTER_KEY,
  DIETARY_RESTRICTIONS_FILTER_KEY,
  BRAND_FILTER_KEY,
  COLOR_FILTER_KEY,
  SIZE_FILTER_KEY,
  PRICE_FILTER_KEY,
} from "@constants/products/listing";
import { DEFAULT_QUANTITY } from "@constants/products/detail";
import {
  LOWEST_PRICE_RANGE_FILTER,
  SystemFilterKeys,
} from "@stadium/shared/src/constants/stores/routes";

import { getCategoryLinkQueryFromFilterOptions } from "@helpers/plasmic/catalog-entries";

const DEFAULT_ARR = Object.freeze([]);

export const getFormattedPriceRanges = (priceRanges, convertIntoStoreCurrency) => {
  const filteredBuckets = priceRanges
    ? priceRanges.buckets.filter((data) => data.doc_count > 0)
    : DEFAULT_ARR;
  return sortBy(
    filteredBuckets.map((data) => {
      if (data.key === "my_budget")
        return { id: data.key, name: "Gifts within my budget", order: 0 };
      if (data.from && data.to)
        return {
          id: data.key,
          name: `${formatPrice(
            convertIntoStoreCurrency(data.from, US_CURRENCY_CODE),
          )} to ${formatPrice(convertIntoStoreCurrency(data.to, US_CURRENCY_CODE))}`,
          order: 1,
        };
      if (data.to)
        return {
          id: data.key,
          name: `Up to ${formatPrice(convertIntoStoreCurrency(data.to, US_CURRENCY_CODE))}`,
          order: 1,
        };
      return {
        id: data.key,
        name: `${formatPrice(convertIntoStoreCurrency(data.from, US_CURRENCY_CODE))}`,
        order: 1,
      };
    }),
    "order",
  );
};

export const getFormattedProductionDays = (optionValues) => {
  const filteredBuckets = optionValues
    ? optionValues.buckets.filter((data) => data.doc_count > 0)
    : DEFAULT_ARR;

  return filteredBuckets.map(({ key }) => {
    return { id: key, name: `${key} ${pluralizeWord("day", { count: key })}` };
  });
};

export const getFormattedOptionValues = (optionValues) => {
  const filteredBuckets = optionValues
    ? optionValues.buckets.filter((data) => data.doc_count > 0)
    : DEFAULT_ARR;

  return filteredBuckets.map(({ key, ...rest }) => {
    return { ...rest, id: key, name: key };
  });
};

export const getFormattedPrintTypeValues = (printTypes) => {
  const filteredBuckets = printTypes?.buckets.filter((data) => data.doc_count > 0) || DEFAULT_ARR;

  return filteredBuckets.map(({ key }) => ({ id: key, name: key }));
};

export const getSwagProductIds = (data) => {
  if (!data) return "";
  const { products } = data.search;
  const swagProductsIds = products
    .filter((product) => isSwagProduct(product.intent))
    .map((product) => product.id);
  return swagProductsIds.join(",");
};

export const getPriceSliderRanges = ({ max: productMaxPrice }, convertIntoStoreCurrency) => {
  const maxPrice =
    Math.ceil(productMaxPrice / parseFloat(PRODUCT_PRICE_SLIDER_STEP)) * PRODUCT_PRICE_SLIDER_STEP;
  return {
    step: convertIntoStoreCurrency(PRODUCT_PRICE_SLIDER_STEP, US_CURRENCY_CODE),
    min: 0,
    max: convertIntoStoreCurrency(maxPrice, US_CURRENCY_CODE),
  };
};

export const getPriceHistogram = (aggregations, convertIntoStoreCurrency) => {
  const buckets = (aggregations?.prices_range?.usd_range_price?.buckets || DEFAULT_ARR).filter(
    (data) => data.key !== "my_budget",
  );

  return buckets
    .map((bucket) => {
      // Handle "20000_*" case where 'to' might be missing
      const to = bucket.to === undefined ? bucket.from * 2 : bucket.to; // Fallback or handle appropriately in UI

      return {
        count: bucket.doc_count,
        from: convertIntoStoreCurrency(bucket.from, US_CURRENCY_CODE),
        to: convertIntoStoreCurrency(to, US_CURRENCY_CODE), // Use calculated 'to'
      };
    })
    .filter((b) => b.count > 0);
};

export const getDiscretePriceRangeOptions = (
  buckets,
  priceStats,
  { displayPrefix, displaySuffix, convertPrice },
) => {
  if (!buckets?.length) return DEFAULT_ARR;

  return buckets
    .map((bucket) => {
      if (bucket.key === "my_budget") return null;

      const from = convertPrice ? convertPrice(bucket.from) : bucket.from;
      const to = convertPrice ? convertPrice(bucket.to) : bucket.to;
      let label = bucket.key;
      let value = bucket.key;

      if (to) {
        label = `${displayPrefix || ""}${to}${displaySuffix || ""} AND UNDER`.toUpperCase();
      } else if (from) {
        label = `${displayPrefix || ""}${from}${displaySuffix || ""} AND ABOVE`.toUpperCase();
        value = priceStats.max >= bucket.from ? `${bucket.from}_${priceStats.max}` : bucket.key;
      }
      return {
        id: bucket.key,
        label,
        value,
      };
    })
    .filter(Boolean);
};

export const getCurrentPriceRangeSelectedInUsd = (priceRangesIds, priceRanges, conversionRates) => {
  const [fromPrice, toPrice] = priceRangesIds;
  const fromPriceInUsd = convertPrice({
    from: priceRanges.currencyCode,
    to: US_CURRENCY_CODE,
    value: fromPrice,
    conversionRates,
  });
  const toPriceInUsd = convertPrice({
    from: priceRanges.currencyCode,
    to: US_CURRENCY_CODE,
    value: toPrice,
    conversionRates,
  });

  return [fromPriceInUsd, toPriceInUsd].join("_");
};

export const getPriceRangeInStoreCurrency = (priceRangesIds, storeCurrency, conversionRates) =>
  priceRangesIds.split("_").map((price) =>
    convertPrice({
      from: US_CURRENCY_CODE,
      to: storeCurrency,
      value: +price,
      conversionRates,
    }),
  );

export const getPriceRangeMarkers = (priceRangeSelected) =>
  priceRangeSelected.map((item) => formatNumberToShortScale(item));

export const getMinOrderableQuantity = (variants) =>
  variants.length > 0
    ? Math.min(...variants.map(({ minOrderableQuantity }) => minOrderableQuantity))
    : DEFAULT_QUANTITY;

export const getFormattedBrandValues = (brands, includeAdditionalFields = false) => {
  const knownBrands = brands
    .filter((brand) => brand.name)
    .sort((a, b) => a.name.localeCompare(b.name));

  const brandsIdMapping = groupBy(knownBrands, "name");
  const sortedBrands = Object.keys(brandsIdMapping).reduce((acc, item) => {
    const brandsWithSameName = brandsIdMapping[item];
    const itemId = brandsWithSameName.map((brand) => brand.id).join("-");
    const brandObject = {
      id: itemId,
      name: item,
    };

    if (includeAdditionalFields) {
      const brand = brandsWithSameName?.[0];
      brandObject.key = brand?.key;
      brandObject.logo = brand?.logo;
    }

    acc.push(brandObject);
    return acc;
  }, []);

  if (sortedBrands.length <= 1) return DEFAULT_ARR;

  return sortedBrands;
};

export const isBulkProduct = (variants) => {
  const minOrderableQtyArr = variants.map(({ minOrderableQuantity }) => minOrderableQuantity);
  return minOrderableQtyArr.some((qty) => qty > 1);
};

export const getMasterVariant = (variants) => variants.find(({ isMaster }) => isMaster);

export const getItemFeatures = ({
  ecoFriendlyProductCount,
  sustainableProductCount,
  shippedFromStadiumProductsCount,
}) => {
  const data = [];
  if (ecoFriendlyProductCount) {
    data.push(ECO_FRIENDLY_FILTER);
  }
  if (sustainableProductCount) {
    data.push(SUSTAINABLE_FILTER);
  }
  if (shippedFromStadiumProductsCount) {
    data.push(SHIPPED_FROM_STADIUM_FILTER);
  }
  return data;
};

export const getUserDesignedStoreIds = ({
  userConfig,
  selectedItemTypes,
  designedItemTypes,
  isLockerShop,
}) => {
  const {
    allLockerSwagIds: allLockerSwagIdsFromApi,
    allOnDemandSwagIds,
    lockerSwagIds: lockerSwagIdsFromApi,
    onDemandSwagIds,
  } = userConfig || {};

  const isOnDemand = selectedItemTypes.includes(ON_DEMAND_FILTER_TYPE.id);
  const isWarehouse = selectedItemTypes.includes(LOCKER_INVENTORY_FILTER_TYPE.id);
  const isDesignedByOthers = designedItemTypes.includes(DESIGNED_BY_OTHERS_FILTER.id);
  const isMyDesigned = designedItemTypes.includes(MY_DESIGNED_PRODUCTS_FILTER.id);
  const allLockerSwagIds = isLockerShop ? DEFAULT_ARR : allLockerSwagIdsFromApi;
  const lockerSwagIds = isLockerShop ? DEFAULT_ARR : lockerSwagIdsFromApi;

  // CASE 1: If "On-Demand" selected
  if (isOnDemand) {
    if (isDesignedByOthers) return difference(allOnDemandSwagIds, onDemandSwagIds);
    if (isMyDesigned) return onDemandSwagIds;
    return allOnDemandSwagIds;
  }

  // CASE 2: Locker Swag
  if (isWarehouse) {
    if (isDesignedByOthers) return difference(allLockerSwagIds, lockerSwagIds);
    if (isMyDesigned) return lockerSwagIds;
    return allLockerSwagIds;
  }

  // CASE 3: "All Products" (Default)
  if (isDesignedByOthers) {
    return union(
      difference(allLockerSwagIds, lockerSwagIds),
      difference(allOnDemandSwagIds, onDemandSwagIds),
    );
  }
  if (isMyDesigned) {
    return union(lockerSwagIds, onDemandSwagIds);
  }

  return union(allLockerSwagIds, allOnDemandSwagIds);
};

export const getFilterQueryVariables = (filterState, brand) => {
  const itemTypes = filterState?.[ITEM_TYPE_FILTER_KEY] || DEFAULT_ARR;
  const itemFeatures = filterState?.[ITEM_FEATURES_FILTER_KEY] || DEFAULT_ARR;

  const isCoreOnDemandFilterSelected = itemTypes.includes(CORE_ON_DEMAND_FILTER_TYPE.id);
  const isAdditionalOnDemandFilterSelected = itemTypes.includes(
    ADDITIONAL_ON_DEMAND_FILTER_TYPE.id,
  );
  const isAnyChildSelected = isCoreOnDemandFilterSelected || isAdditionalOnDemandFilterSelected;

  const noMinQuantity =
    (isCoreOnDemandFilterSelected && isAdditionalOnDemandFilterSelected) ||
    (itemTypes.includes(ON_DEMAND_FILTER_TYPE.id) && !isAnyChildSelected);
  const bulkProducts = itemTypes.includes(BULK_ITEM_FILTER_TYPE.id);
  const hasTierPricing = itemTypes.includes(VOLUME_DISCOUNT_FILTER.id);
  const coreOnDemand = !noMinQuantity && isCoreOnDemandFilterSelected;
  const additionalOnDemand = !noMinQuantity && isAdditionalOnDemandFilterSelected;

  const ecoFriendly = itemFeatures.some((id) => id === ECO_FRIENDLY_FILTER.id);
  const sustainable = itemFeatures.some((id) => id === SUSTAINABLE_FILTER.id);
  const shippedFromStadium = itemFeatures.some((id) => id === SHIPPED_FROM_STADIUM_FILTER.id);

  const activeFlattenedBrandValueIds = filterState?.[BRAND_FILTER_KEY]
    ? filterState[BRAND_FILTER_KEY].flatMap((id) => String(id).split("-")).map(Number)
    : DEFAULT_ARR;

  const brandIds = !brand || isEmpty(brand) ? activeFlattenedBrandValueIds : [+brand.id];
  const priceValue = filterState?.price;
  let priceKeys = DEFAULT_ARR;
  if (priceValue) {
    if (Array.isArray(priceValue)) {
      priceKeys = priceValue;
    } else if (typeof priceValue === "string") {
      priceKeys = [priceValue];
    } else if (typeof priceValue === "object") {
      priceKeys = [`${priceValue.min || 0}_${priceValue.max || 0}`];
    }
  }

  const resolvedPriceKeys = priceKeys.filter((key) => key !== LOWEST_PRICE_RANGE_FILTER);
  const priceKeysForApi = resolvedPriceKeys.length ? resolvedPriceKeys : DEFAULT_ARR;

  const variables = {
    productionDays: (filterState?.[PRODUCTION_DAYS_FILTER_KEY] || []).map(Number),
    dietaryRestrictionIds:
      filterState?.[DIETARY_RESTRICTIONS_FILTER_KEY]?.map(Number) || DEFAULT_ARR,
    brandIds,
    priceKeys: priceKeysForApi,
    color: filterState?.[COLOR_FILTER_KEY] || DEFAULT_ARR,
    size: filterState?.[SIZE_FILTER_KEY] || DEFAULT_ARR,
    printTypes: filterState?.[DECORATION_METHOD_KEY] || DEFAULT_ARR,
    noMinQuantity,
    coreOnDemand,
    additionalOnDemand,
    bulkProducts,
    ecoFriendly,
    ...(shippedFromStadium && { shippedFromStadium }),
    ...(sustainable && { sustainable }),
  };

  if (hasTierPricing) {
    variables.hasTierPricing = true;
  }

  return variables;
};

export const getFilterQueryVariablesFromFilterOptions = (filterOptions) => {
  if (!filterOptions) return {};
  const query = getCategoryLinkQueryFromFilterOptions(filterOptions);
  if (isEmpty(query)) return {};

  const filterState = {};
  let sortByOverride;

  if (query[SystemFilterKeys.SORT]) {
    sortByOverride = query[SystemFilterKeys.SORT];
  }

  const itemTypeQueryKey = `${ITEM_TYPE_FILTER_KEY}[]`;
  const itemFeaturesQueryKey = `${ITEM_FEATURES_FILTER_KEY}[]`;

  if (query[itemTypeQueryKey]) {
    filterState[ITEM_TYPE_FILTER_KEY] = query[itemTypeQueryKey].split(",").filter(Boolean);
  }
  if (query[itemFeaturesQueryKey]) {
    filterState[ITEM_FEATURES_FILTER_KEY] = [query[itemFeaturesQueryKey]];
  }
  if (query[PRICE_FILTER_KEY]) {
    filterState[PRICE_FILTER_KEY] = query[PRICE_FILTER_KEY];
  }

  const filterVars = getFilterQueryVariables(filterState);
  const { priceKeys, ...filterVarsWithoutPriceKeys } = filterVars;

  return {
    ...filterVarsWithoutPriceKeys,
    ...(query[PRICE_FILTER_KEY] ? { priceKeys } : {}),
    ...(sortByOverride ? { sortBy: sortByOverride } : {}),
  };
};
